import React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog'
import { Text } from '@wartek-id/text'
import { Spinner } from '@wartek-id/spinner'

import Header from 'app/BuktiKaryaPage/components/Header'
import PDFPreviewer from 'app/BuktiKaryaPage/components/PDFPreviewer'

type GuideProps = {
  documentName?: string
}

export const BCKSInvitationGuide = (props: GuideProps) => {
  const router = useRouter()
  const { documentName = null } = props
  const [pageLoaded, setPageLoaded] = React.useState(false)
  const [fileIsLoading, setFileIsLoading] = React.useState(true)
  const { title = '', docUrl = '', signedDocUrl = false } = router.query

  let docUrlSigned = docUrl

  try {
    if (!documentName && docUrl && signedDocUrl) {
      // add more if using route query
      const nonSignedQuery = ['title', 'docUrl', 'signedDocUrl']
      const additionalSignedUrlQuery = Object.keys(router.query).filter(
        (key) => !nonSignedQuery.includes(key)
      )
      const additionalSignedUrlQueryStr = additionalSignedUrlQuery
        .map((key) => `${key}=${router.query[key]}`)
        .join('&')
      docUrlSigned = `${docUrl}&${additionalSignedUrlQueryStr}`
    }
  } catch (error) {
    console.error(error)
  }

  const documentMap = {
    kinerja: {
      title: 'Hasil Penilaian Kinerja Guru',
      subTitle:
        'Hasil penilaian kinerja dengan sebutan paling rendah Baik selama 2 tahun terakhir untuk setiap unsur penilaian.',
      actionText: 'Lihat contoh dokumen',
      src: 'https://cdn.guru-ks-fe.belajar.id/documents/(censored)%20Laporan%20Hasil%20penilaian%20Kinerja%20Guru.pdf',
    },
    managerial: {
      title: 'Surat Keterangan Pengalaman Manajerial',
      subTitle: (
        <>
          Surat keterangan dari Kepala Sekolah bahwa Anda memiliki pengalaman
          manajerial paling singkat 2 tahun di satuan pendidikan, organisasi
          pendidikan, dan/atau komunitas pendidikan.
          <br /> *Khusus PPPK lampirkan juga bukti pengalaman kerja di dalam 1
          dokumen yang sama
        </>
      ),
      actionText: 'Lihat contoh dokumen',
      src: 'https://cdn.guru-ks-fe.belajar.id/documents/(censored)%20KepKepsek%20Penugasan%20Guru%20sebagai%20Kepala%20Perpustakaan.pdf',
    },
    discipline: {
      title: 'Surat Keterangan Bebas Hukuman Disiplin',
      subTitle:
        'Surat keterangan dari Kepala Sekolah yang diterbitkan dalam 2 tahun terakhir bahwa Anda tidak pernah dikenai hukuman disiplin sedang dan/atau berat sesuai dengan ketentuan peraturan perundang-undangan (tidak perlu meterai).',
      actionText: 'Lihat contoh dokumen',
      src: 'https://cdn.guru-ks-fe.belajar.id/documents/(censored)%20Surat%20Pernyataan%20Tidak%20Pernah%20Dijatuhi%20Hukdis.pdf',
    },
    police: {
      title: 'Surat Keterangan Catatan Kepolisian (masa berlaku 6 bulan)',
      subTitle:
        'Surat keterangan perilaku baik dari Polres setempat yang diterbitkan dalam 6 bulan terakhir bahwa Anda tidak sedang menjadi tersangka, terdakwa, atau tidak pernah menjadi terpidana.',
      actionText: 'Lihat contoh dokumen',
      src: 'https://cdn.guru-ks-fe.belajar.id/documents/Surat%20Keterangan%20Catatan%20Kepolisian.pdf',
    },
    pakta_integritas: {
      title: 'Pakta Integritas',
      subTitle: 'Dokumen perjanjian yang ditandatangani.',
      actionText: 'Lihat contoh dokumen',
      src: 'https://cdn.guru-ks-fe.belajar.id/documents/Pakta%20Integritas.pdf',
    },
  }

  React.useEffect(() => {
    const onPageLoad = () => {
      setPageLoaded(true)
    }

    // Check if the page has already loaded
    if (document.readyState === 'complete') {
      onPageLoad()
    } else {
      window.addEventListener('load', onPageLoad)
      // Remove the event listener when component unmounts
      return () => window.removeEventListener('load', onPageLoad)
    }
  }, [])

  if (!documentName && !title && !docUrl)
    return (
      <div className="relative flex flex-col h-screen max-w-xl mx-auto">
        <Header title="Panduan Dokumen" forceNavigateBack />
        {/* TODO: @egi uncomment when link ready */}
        {/* {props.isPaktaIntegritasEnabled && (
          <div className="bg-surface-selected-default p-4 flex flex-col gap-4">
            <Text variant="body-sm">
              Catatan: Baca petunjuk teknis untuk info dokumen selengkapnya
            </Text>
            <Link passHref href={`#`}>
              <a>
                <Text
                  variant="action"
                  style={{ fontWeight: 'bold' }}
                  color="primary"
                  className="cursor-pointer"
                >
                  Baca Petunjuk Teknis
                </Text>
              </a>
            </Link>
          </div>
        )} */}

        {Object.keys(documentMap).map((key) => {
          return (
            <section
              key={key}
              className="flex flex-col w-full bg-white p-4 border-b border-subdued"
            >
              <Text variant="heading-sm">{documentMap[key].title}</Text>
              <Text color="subdued" className="my-2">
                {documentMap[key].subTitle}
              </Text>
              <Link passHref href={`/bcks/invitation/guide/${key}`}>
                <a>
                  <Text
                    variant="body-sm"
                    style={{ fontWeight: 'bold' }}
                    color="primary"
                    className="cursor-pointer"
                  >
                    {documentMap[key].actionText}
                  </Text>
                </a>
              </Link>
            </section>
          )
        })}
        <div></div>
      </div>
    )

  return (
    <div className="relative flex flex-col h-screen max-w-xl mx-auto">
      <Header
        title={title || documentMap[documentName].title}
        forceNavigateBack
      />
      <Dialog isOpen={fileIsLoading} onClose={null}>
        <DialogOverlay />
        <DialogContent>
          <div className="flex flex-row content-around sticky w-full p-2">
            <Spinner size="lg" />
            <Text as="span" className="pl-4" variant="body">
              Harap Tunggu, Tinjauan File Sedang Diproses
            </Text>
          </div>
        </DialogContent>
      </Dialog>
      {/* <div className="mt-4"> */}
      <PDFPreviewer
        data={docUrlSigned || documentMap[documentName].src}
        onFilePreviewClick={() => null}
        pageLoaded={pageLoaded}
        setFileIsLoading={setFileIsLoading}
        isRemoteUrl
      />
      {/* </div> */}
    </div>
  )
}
